import { DEFAULT_CHAT_LANGUAGE, useApp, useUserSettings } from "@next-platform/client-common-core";
import { useEffect, useState } from "react";
import InstantSearchAdapter from "typesense-instantsearch-adapter";

import { SearchAdapter } from "@/components/search/types";
import { createSearchClientConfig } from "@/components/search/utils/createSearchClientConfig";
import {
  DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT,
  DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_LOREBOOKS,
  DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT,
  DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_LOREBOOKS,
} from "@/constants";
import { Collection, TypesenseApiKey } from "@/types/api";

interface UseSharedChatbotsSearchProps {
  shouldDelayQuery: boolean;
  collection: Collection;
}

interface UseSharedChatbotsSearchReturn {
  instantSearchAdapter: null | SearchAdapter;
  queryBy: string;
  includeFields: string;
  isReady: boolean;
}

export const useSharedChatbotsSearch = ({
  shouldDelayQuery,
  collection = Collection.CHARACTERS,
}: UseSharedChatbotsSearchProps): UseSharedChatbotsSearchReturn => {
  const { typesenseConfig: searchClientRemoteConfig } = useApp();
  const { chatLanguage } = useUserSettings();
  const [instantSearchAdapter, setInstantSearchAdapter] = useState<null | SearchAdapter>(null);
  const [queryBy, setQueryBy] = useState(DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT);
  const [includeFields, setIncludeFields] = useState(DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT);
  const apiKey = searchClientRemoteConfig?.[TypesenseApiKey[collection]];
  useEffect(() => {
    if (chatLanguage && chatLanguage !== DEFAULT_CHAT_LANGUAGE) {
      // The lorebooks collection has no name_i18n field in its Typesense schema, so appending it 404s the query.
      let queryByValue = DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT + `,title_i18n.${chatLanguage},name_i18n.${chatLanguage}`;
      if (collection === Collection.LOREBOOKS) {
        queryByValue = DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT_LOREBOOKS;
      }
      setQueryBy(queryByValue);
      let includeFieldsValue = DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT + `,title_i18n.${chatLanguage},name_i18n.${chatLanguage}`;
      if (collection === Collection.LOREBOOKS) {
        includeFieldsValue = DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT_LOREBOOKS;
      }
      setIncludeFields(includeFieldsValue);
    }
  }, [chatLanguage]);

  useEffect(() => {
    if (apiKey && !shouldDelayQuery) {
      const searchClientConfig = createSearchClientConfig({
        apiKey,
        queryBy,
        includeFields,
      });

      const adapter = new InstantSearchAdapter(searchClientConfig);
      setInstantSearchAdapter(adapter as SearchAdapter);
    }
  }, [apiKey, shouldDelayQuery, queryBy, includeFields]);

  useEffect(() => {
    if (instantSearchAdapter) {
      instantSearchAdapter.configuration.additionalSearchParameters.query_by = queryBy;
      instantSearchAdapter.configuration.additionalSearchParameters.include_fields = includeFields;
    }
  }, [queryBy, includeFields, instantSearchAdapter]);

  const isReady = !!instantSearchAdapter?.searchClient && !!apiKey;

  return {
    instantSearchAdapter,
    queryBy,
    includeFields,
    isReady,
  };
};
